















































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ajax from '@/lib/ajax';
import { PayrollRunDetailsModule, PayrollRunErrorDto } from '@/store/modules/payrollRunDetails';

interface ControlJob {
    controlJob: string;
    completed: boolean;
    processing: boolean | null;
    user: string;
    lastUpdated: string | null;
}

// Mirrors PAYROLL_RUN_PHASE_* in api/core/payroll_runs.go — used only for
// the Errors tab's label column.
const PHASE_LABELS: { [key: number]: string } = {
    30: 'Submitting',
    40: 'Submitted',
    50: 'Syncing data',
};

// Mirrors payrollTaskLabel / TASK_PATH_PAYROLL_* in api/core/payroll.go —
// display names for the background task currently holding the run's lock.
const PAYROLL_TASK_LABELS: { [key: string]: string } = {
    'payroll-ensure-workers': 'Send workers',
    'payroll-ensure-customers': 'Send locations',
    'payroll-submit-timesheets': 'Send timesheets',
    'payroll-fetch-payslips': 'Fetch payslips',
    'payroll-fetch-holiday': 'Fetch holiday',
};

@Component({
    name: 'PayrollRunPurePro',
})
export default class extends GiganautVue {
    @Prop({ required: true }) runId!: string;

    activeTab = 0;

    errorsLoading = false;

    jobsLoading = false;
    controlJobs: ControlJob[] | null = null;
    jobsError = '';

    errorHeaders = [
        { text: 'When', value: 'createdAt', sortable: false, width: 180 },
        { text: 'Phase', value: 'phase', sortable: false, width: 140 },
        { text: 'Message', value: 'message', sortable: false },
    ];

    jobHeaders = [
        { text: 'Job', value: 'controlJob', sortable: false },
        { text: 'Completed', value: 'completed', sortable: false, width: 110 },
        { text: 'Processing', value: 'processing', sortable: false, width: 110 },
        { text: 'User', value: 'user', sortable: false, width: 180 },
        { text: 'Last updated', value: 'lastUpdated', sortable: false, width: 180 },
    ];

    get errors(): PayrollRunErrorDto[] { return PayrollRunDetailsModule.errors; }
    get storeErrorsLoading(): boolean { return PayrollRunDetailsModule.errorsLoading; }

    // The run's own period reference — everything on this tab is scoped
    // to it.
    get runPeriodId(): string {
        const run = PayrollRunDetailsModule.run;
        return run ? run.externalPayrollSystemPeriodId : '';
    }

    runRefreshing = false;

    // Non-empty while the run's background task lock is held — the label of
    // the task believed to be running. (An orphaned lock self-expires
    // server-side after 10 minutes, so a stale "running" here resolves
    // itself on the next refresh.)
    get runningTaskLabel(): string {
        const run = PayrollRunDetailsModule.run;
        if (!run || !run.backgroundTaskLockedAt) return '';
        return PAYROLL_TASK_LABELS[run.backgroundTaskType] || 'Background sync';
    }

    get runningTaskSince(): string {
        const run = PayrollRunDetailsModule.run;
        if (!run || !run.backgroundTaskLockedAt) return '';
        return this.formatDateTime(run.backgroundTaskLockedAt);
    }

    async refreshRun() {
        this.runRefreshing = true;
        try {
            await PayrollRunDetailsModule.fetch(this.runId);
        } finally {
            this.runRefreshing = false;
        }
    }

    async mounted() {
        // Control jobs (tab 0) are visible immediately; errors load too so
        // the ERRORS tab count is meaningful before it's visited.
        await Promise.all([
            this.loadControlJobs(),
            PayrollRunDetailsModule.ensureErrorsLoaded(this.runId),
        ]);
    }

    // Sub-tabs load on first activation only; the per-tab refresh buttons
    // force a reload from PurePro afterwards.
    @Watch('activeTab')
    async onActiveTabChanged(tabIdx: number) {
        if (tabIdx === 0 && this.controlJobs === null && !this.jobsLoading) {
            await this.loadControlJobs();
        } else if (tabIdx === 1) {
            await PayrollRunDetailsModule.ensureErrorsLoaded(this.runId);
        }
    }

    async refreshErrors() {
        this.errorsLoading = true;
        try {
            await PayrollRunDetailsModule.fetchErrors(this.runId);
        } finally {
            this.errorsLoading = false;
        }
    }

    async loadControlJobs() {
        this.jobsLoading = true;
        this.jobsError = '';
        try {
            // Control jobs are fetched for THIS RUN's period — not the
            // account's current period, which may have rolled on since.
            const run = PayrollRunDetailsModule.run;
            const periodId = run ? run.externalPayrollSystemPeriodId : '';
            if (!periodId) {
                this.jobsError = 'The payroll run has no period reference yet';
                return;
            }
            const response = await ajax.get(`/api/v1/agency/payroll/pure-pro/control-jobs?periodId=${encodeURIComponent(periodId)}`);
            this.controlJobs = response.data.controlJobs || [];
        } catch (e: any) {
            this.jobsError = this.extractError(e) || 'Failed to load control jobs from PurePro';
        } finally {
            this.jobsLoading = false;
        }
    }

    phaseLabel(phase: number): string {
        return PHASE_LABELS[phase] || `Phase ${phase}`;
    }

    formatDateTime(s: string | null): string {
        if (!s) return '';
        const d = new Date(s);
        return isNaN(d.getTime()) ? '' : d.toLocaleString();
    }

    extractError(e: any): string {
        if (!e) return '';
        if (e.response && e.response.data && e.response.data.message) return e.response.data.message;
        if (e.message) return e.message;
        return '';
    }
}
